<template>
  <SplitPanelShell
    class="split-panel-shell--mail"
    title="Inbox"
    icon="bi bi-inbox-fill"
    :toolbar-columns="1"
    :count="isActiveServiceConnected && filteredEmails.length ? filteredEmails.length : null"
    :status-message="statusMessage"
    :status-type="statusType"
  >
    <template #actions>
      <PrimaryButton
        v-if="!isActiveServiceConnected"
        :icon="activeService === 'outlook' ? 'bi bi-microsoft' : 'bi bi-envelope'"
        :label="connecting ? 'Connecting...' : 'Connect'"
        :loading="connecting"
        :disabled="connecting"
        compact
        @click="handleConnect"
      />
      <template v-else>
        <SecondaryButton
          icon="bi bi-arrow-clockwise"
          label=""
          custom-class="split-panel-shell__icon-action"
          :loading="loadingEmails"
          :disabled="loadingEmails"
          title="Refresh inbox"
          @click="loadEmails"
        />
        <SecondaryButton
          icon="bi bi-box-arrow-right"
          label=""
          custom-class="split-panel-shell__icon-action"
          :loading="disconnecting"
          :disabled="disconnecting"
          title="Disconnect"
          @click="handleDisconnect"
        />
      </template>
    </template>

    <template #account>
      <div class="split-panel-shell__service-pills" role="tablist">
        <button
          type="button"
          role="tab"
          class="split-panel-shell__service-pill"
          :class="{ 'split-panel-shell__service-pill--active': activeService === 'outlook' }"
          @click="selectService('outlook')"
        >
          <i class="bi bi-microsoft" aria-hidden="true"></i>
          Outlook
        </button>
        <button
          type="button"
          role="tab"
          class="split-panel-shell__service-pill"
          :class="{ 'split-panel-shell__service-pill--active': activeService === 'gmail' }"
          @click="selectService('gmail')"
        >
          <i class="bi bi-envelope" aria-hidden="true"></i>
          Gmail
        </button>
      </div>
      <span
        v-if="isActiveServiceConnected && activeUserEmail"
        class="split-panel-shell__account-email"
        :title="activeUserEmail"
      >
        <i class="bi bi-person-circle" aria-hidden="true"></i>
        {{ activeUserEmail }}
      </span>
    </template>

    <template #toolbar>
      <LabelSearchField
        input-id="draft-bills-mail-search"
        v-model="searchQuery"
        label="Search loaded mail"
        placeholder="Filter loaded mail..."
        :disabled="!isActiveServiceConnected || emails.length === 0"
      />
    </template>

    <template #list>
      <div v-if="!isActiveServiceConnected" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-envelope-open" aria-hidden="true"></i>
        </div>
        <h4>Connect your mail</h4>
        <p>Link Outlook or Gmail to pull vendor bill emails.</p>
        <PrimaryButton
          :icon="activeService === 'outlook' ? 'bi bi-microsoft' : 'bi bi-envelope'"
          :label="connecting ? 'Connecting...' : connectLabel"
          :loading="connecting"
          :disabled="connecting"
          @click="handleConnect"
        />
      </div>

      <div v-else-if="needsMailSourceSetup" class="split-panel-shell__empty">
        <i class="bi bi-envelope-check" aria-hidden="true"></i>
        <h4>Configure mail sources</h4>
        <p>
          Use <strong>Bill Mail Setup</strong> to add vendor sender emails.
          Only mail from those senders will be loaded.
        </p>
        <PrimaryButton
          icon="bi bi-envelope-check"
          label="Open Bill Mail Setup"
          compact
          @click="requestMailConfiguration"
        />
      </div>

      <div v-else-if="loadingEmails && emails.length === 0" class="split-panel-shell__empty">
        <i class="bi bi-arrow-repeat spinning" aria-hidden="true"></i>
        <p>Loading mail from configured senders...</p>
      </div>

      <div v-else-if="filteredEmails.length === 0" class="split-panel-shell__empty">
        <i class="bi bi-inbox" aria-hidden="true"></i>
        <p>{{ emptyListMessage }}</p>
      </div>

      <div v-else class="mail-inbox-list-wrap">
        <div class="split-panel-shell__list-scroll mail-inbox-list">
          <template v-for="group in groupedEmails" :key="group.label">
            <div class="mail-inbox-group">{{ group.label }}</div>
            <button
              v-for="email in group.emails"
              :key="email.id"
              type="button"
              class="mail-inbox-row"
              :class="{ 'mail-inbox-row--selected': selectedEmail?.id === email.id }"
              @click="selectEmail(email)"
            >
              <span
                class="mail-inbox-row__avatar"
                :style="recipientAvatarStyle(resolveSenderEmail(email))"
                aria-hidden="true"
              >{{ senderInitials(email) }}</span>
              <span class="mail-inbox-row__main">
                <span class="mail-inbox-row__top">
                  <span class="mail-inbox-row__sender">
                    {{ senderName(email) }}
                    <i
                      v-if="email.hasAttachments"
                      class="bi bi-paperclip mail-inbox-row__attach-icon"
                      aria-label="Has attachments"
                      title="Has attachments"
                    ></i>
                  </span>
                  <span class="mail-inbox-row__date">{{ formatDateShort(email.receivedDateTime || email.date) }}</span>
                </span>
                <span class="mail-inbox-row__subject">{{ email.subject || '(No subject)' }}</span>
                <span class="mail-inbox-row__preview">{{ email.bodyPreview || email.snippet || '' }}</span>
              </span>
            </button>
          </template>
        </div>

        <MailInboxPagination
          :current-page="currentPage"
          :total-items="filteredEmails.length"
          :items-per-page="itemsPerPage"
          :show-items-per-page="true"
          :per-page-options="mailPerPageOptions"
          @page-change="handlePageChange"
          @items-per-page-change="handleItemsPerPageChange"
        />
      </div>
    </template>

    <template #detail>
      <div v-if="!selectedEmail" class="split-panel-shell__detail-empty">
        <i class="bi bi-envelope-paper" aria-hidden="true"></i>
        <p>Select a message to read</p>
      </div>
      <template v-else>
        <div class="mail-reader-scroll">
        <div class="mail-reader">
          <div class="mail-reader__top">
            <h4 class="mail-reader__subject">{{ selectedEmail.subject || '(No subject)' }}</h4>
            <SecondaryButton
              icon="bi bi-x-lg"
              label=""
              custom-class="split-panel-shell__icon-action mail-reader__close"
              title="Close"
              @click="selectedEmail = null"
            />
          </div>

          <div class="mail-reader__header">
            <div class="mail-reader__from-row">
              <span
                class="mail-reader__avatar mail-reader__avatar--from"
                :style="recipientAvatarStyle(fromEmailAddress)"
                aria-hidden="true"
              >
                {{ senderInitials(selectedEmail) }}
              </span>
              <div class="mail-reader__from-main">
                <div class="mail-reader__from-line">
                  <strong class="mail-reader__from-name">{{ senderName(selectedEmail) }}</strong>
                  <time class="mail-reader__date">
                    {{ formatDateFull(selectedEmail.receivedDateTime || selectedEmail.date) }}
                  </time>
                </div>
                <span v-if="fromEmailAddress" class="mail-reader__from-email">&lt;{{ fromEmailAddress }}&gt;</span>
              </div>
            </div>

            <div v-if="toRecipients.length" class="mail-reader__field">
              <span class="mail-reader__label">To</span>
              <div class="mail-reader__recipients">
                <MailRecipientPill
                  v-for="recipient in toRecipients"
                  :key="`to-${recipient.email}`"
                  :recipient="recipient"
                />
              </div>
            </div>

            <div v-if="ccRecipients.length" class="mail-reader__field">
              <span class="mail-reader__label">Cc</span>
              <div class="mail-reader__recipients">
                <MailRecipientPill
                  v-for="recipient in ccRecipients"
                  :key="`cc-${recipient.email}`"
                  :recipient="recipient"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="emailAttachments.length > 0"
          class="mail-attachments"
        >
          <div class="mail-attachments__title">
            <i class="bi bi-paperclip" aria-hidden="true"></i>
            <span>{{ emailAttachments.length }} attachment{{ emailAttachments.length > 1 ? 's' : '' }}</span>
          </div>
          <ul class="mail-attachments__grid">
            <li
              v-for="attachment in emailAttachments"
              :key="attachment.id"
              class="mail-attachments__item"
            >
              <button
                type="button"
                class="mail-attachment-card"
                :class="attachmentCardClass(attachment.name)"
                :disabled="downloadingAttachmentId === attachment.id"
                @click="downloadAttachment(attachment)"
              >
                <span class="mail-attachment-card__icon" aria-hidden="true">
                  <i :class="attachmentFileIcon(attachment.name)"></i>
                </span>
                <span class="mail-attachment-card__meta">
                  <span class="mail-attachment-card__name">{{ attachment.name }}</span>
                  <span class="mail-attachment-card__size">{{ formatFileSize(attachment.size) }}</span>
                </span>
                <i
                  v-if="downloadingAttachmentId === attachment.id"
                  class="bi bi-arrow-repeat spinning mail-attachment-card__spinner"
                  aria-hidden="true"
                ></i>
                <i v-else class="bi bi-download mail-attachment-card__download" aria-hidden="true"></i>
              </button>
            </li>
          </ul>
        </div>
        <div
          v-if="selectedEmail.body"
          ref="mailBodyHtml"
          class="mail-reader-body mail-reader-body--html"
          v-html="renderedEmailBody"
        ></div>
        <div v-else class="mail-reader-body mail-reader-body--plain">
          {{ selectedEmail.bodyPreview || selectedEmail.snippet || 'No content available.' }}
        </div>
        </div>
      </template>
    </template>
  </SplitPanelShell>
</template>

<script>
import logger from '@/utils/logger';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import LabelSearchField from '@/modules/consultant/components/LabelSearchField.vue';
import MailInboxPagination from '@/modules/vendor/components/MailInboxPagination.vue';
import MailRecipientPill from '@/modules/vendor/components/MailRecipientPill.vue';
import SplitPanelShell from '@/modules/vendor/components/SplitPanelShell.vue';
import {
  nameInitials,
  formatOutlookListDate,
  formatOutlookGroupLabel,
  formatOutlookDetailDate,
  resolveEmailRecipients,
  recipientAvatarStyle,
  recipientDisplayName,
} from '@/modules/vendor/utils/splitPanelUtils';
import {
  buildMailFetchQuery,
  hasConfiguredMailSources,
  describeMailFetchRules,
} from '@/modules/vendor/utils/mailInboxFetchRules';

const STORAGE_KEY_MAIL_PAGE_SIZE = 'draft-bills-mail-items-per-page';
const MAIL_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const MAIL_FETCH_CAP = 200;

function loadSavedMailPageSize() {
  try {
    const saved = parseInt(localStorage.getItem(STORAGE_KEY_MAIL_PAGE_SIZE), 10);
    if (MAIL_PER_PAGE_OPTIONS.includes(saved)) return saved;
  } catch (e) {
    /* ignore */
  }
  return 25;
}

function computeMailFetchSize(itemsPerPage) {
  return Math.min(Math.max(itemsPerPage * 10, 50), MAIL_FETCH_CAP);
}

export default {
  name: 'DraftBillsMailPanel',
  components: {
    PrimaryButton,
    SecondaryButton,
    LabelSearchField,
    MailInboxPagination,
    MailRecipientPill,
    SplitPanelShell,
  },
  props: {
    fetchRules: {
      type: Object,
      required: true,
    },
  },
  emits: ['configure-mail', 'emails-updated', 'connection-updated'],
  data() {
    return {
      activeService: 'outlook',
      gmailAuthenticated: false,
      outlookAuthenticated: false,
      gmailUserEmail: '',
      outlookUserEmail: '',
      connecting: false,
      disconnecting: false,
      loadingEmails: false,
      statusMessage: '',
      statusType: '',
      authCheckInterval: null,
      emails: [],
      searchQuery: '',
      selectedEmail: null,
      downloadingAttachmentId: null,
      currentPage: 1,
      itemsPerPage: loadSavedMailPageSize(),
      mailPerPageOptions: MAIL_PER_PAGE_OPTIONS,
      billMailLiveSubscribed: false,
      liveSyncHintShown: false,
      lastIngestedIds: {},
      doneMessageIds: {},
    };
  },
  computed: {
    companyId() {
      return this.$route?.params?.id || this.$route?.params?.companyId || null;
    },
    isLiveSyncMode() {
      return String(this.fetchRules?.syncMode || '').toLowerCase() === 'immediate';
    },
    isActiveServiceConnected() {
      return this.activeService === 'gmail' ? this.gmailAuthenticated : this.outlookAuthenticated;
    },
    activeUserEmail() {
      return this.activeService === 'gmail' ? this.gmailUserEmail : this.outlookUserEmail;
    },
    connectLabel() {
      return this.activeService === 'outlook' ? 'Connect Outlook' : 'Connect Gmail';
    },
    needsMailSourceSetup() {
      return this.isActiveServiceConnected && !hasConfiguredMailSources(this.fetchRules);
    },
    emptyListMessage() {
      if (this.searchQuery) return 'No loaded messages match your search.';
      return 'No mail found from your configured senders. Update Bill Mail Setup and refresh.';
    },
    filteredEmails() {
      if (!this.searchQuery) return this.emails;
      const q = this.searchQuery.toLowerCase();
      return this.emails.filter((email) => {
        const subject = (email.subject || '').toLowerCase();
        const from = (email.from || email.fromEmail || '').toLowerCase();
        const preview = (email.bodyPreview || email.snippet || '').toLowerCase();
        return subject.includes(q) || from.includes(q) || preview.includes(q);
      });
    },
    paginatedEmails() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredEmails.slice(start, start + this.itemsPerPage);
    },
    groupedEmails() {
      const groups = [];
      const indexByLabel = new Map();

      this.paginatedEmails.forEach((email) => {
        const label = formatOutlookGroupLabel(email.receivedDateTime || email.date);
        if (!indexByLabel.has(label)) {
          indexByLabel.set(label, groups.length);
          groups.push({ label, emails: [] });
        }
        groups[indexByLabel.get(label)].emails.push(email);
      });

      return groups;
    },
    emailAttachments() {
      if (!this.selectedEmail || !Array.isArray(this.selectedEmail.attachments)) {
        return [];
      }
      return this.selectedEmail.attachments.filter((attachment) => attachment?.id);
    },
    fromEmailAddress() {
      return this.resolveSenderEmail(this.selectedEmail);
    },
    toRecipients() {
      return resolveEmailRecipients(this.selectedEmail, 'to');
    },
    ccRecipients() {
      return resolveEmailRecipients(this.selectedEmail, 'cc');
    },
    renderedEmailBody() {
      const html = this.selectedEmail?.body;
      if (!html) return '';
      return String(html).replace(/<img\b([^>]*)>/gi, (match, attrs) => {
        let next = attrs || '';
        if (!/\breferrerpolicy\s*=/i.test(next)) {
          next += ' referrerpolicy="no-referrer"';
        }
        if (!/\bloading\s*=/i.test(next)) {
          next += ' loading="lazy"';
        }
        return `<img${next}>`;
      });
    },
  },
  watch: {
    emails: {
      deep: true,
      handler(list) {
        this.$emit('emails-updated', Array.isArray(list) ? list : []);
      },
    },
    isActiveServiceConnected: {
      immediate: true,
      handler(connected) {
        this.$emit('connection-updated', !!connected);
        if (connected) {
          if (hasConfiguredMailSources(this.fetchRules)) {
            this.loadEmails();
          } else {
            // Stay on Draft Bills — show setup empty state; do not auto-navigate
            this.emails = [];
            this.selectedEmail = null;
          }
        } else {
          this.emails = [];
          this.selectedEmail = null;
          this.currentPage = 1;
        }
        this.syncBillMailLiveSubscription();
      },
    },
    activeService() {
      this.selectedEmail = null;
      this.emails = [];
      this.searchQuery = '';
      this.currentPage = 1;
      if (this.isActiveServiceConnected) {
        if (hasConfiguredMailSources(this.fetchRules)) {
          this.loadEmails();
        } else {
          this.emails = [];
          this.selectedEmail = null;
        }
      }
    },
    searchQuery() {
      this.currentPage = 1;
    },
    selectedEmail: {
      handler() {
        this.$nextTick(() => this.bindMailBodyImages());
      },
    },
    renderedEmailBody() {
      this.$nextTick(() => this.bindMailBodyImages());
    },
    fetchRules: {
      deep: true,
      handler() {
        if (!this.isActiveServiceConnected) return;
        this.currentPage = 1;
        this.selectedEmail = null;
        if (hasConfiguredMailSources(this.fetchRules)) {
          this.loadEmails();
        } else {
          this.emails = [];
        }
        this.syncBillMailLiveSubscription();
      },
    },
    isLiveSyncMode() {
      this.syncBillMailLiveSubscription();
    },
  },
  mounted() {
    this.checkEmailServiceAuth();
    this.onBillMailRefresh = (event) => this.handleBillMailSocketRefresh(event);
    window.addEventListener('bill-mail-refresh', this.onBillMailRefresh);
    this.syncBillMailLiveSubscription();
    this.$emit('emails-updated', this.emails);
    this.$emit('connection-updated', this.isActiveServiceConnected);
  },
  beforeUnmount() {
    this.clearAuthCheckInterval();
    window.removeEventListener('bill-mail-refresh', this.onBillMailRefresh);
    this.stopBillMailLiveSubscription();
  },
  methods: {
    getEmails() {
      return Array.isArray(this.emails) ? [...this.emails] : [];
    },
    isConnected() {
      return !!this.isActiveServiceConnected;
    },
    requestMailConfiguration() {
      this.$emit('configure-mail');
    },
    syncBillMailLiveSubscription() {
      const shouldWatch =
        this.isLiveSyncMode &&
        this.isActiveServiceConnected &&
        hasConfiguredMailSources(this.fetchRules) &&
        this.companyId;
      if (shouldWatch) this.startBillMailLiveSubscription();
      else this.stopBillMailLiveSubscription();
    },
    startBillMailLiveSubscription() {
      if (!this.companyId || this.billMailLiveSubscribed) return;
      import('@/services/companyPresenceClient')
        .then((m) => {
          m.connectCompanyPresence();
          m.subscribeBillMailLive(this.companyId);
          this.billMailLiveSubscribed = true;
          if (!this.liveSyncHintShown) {
            this.liveSyncHintShown = true;
            this.statusMessage = 'Live sync on — new matching mail loads automatically.';
            this.statusType = 'info';
            setTimeout(() => {
              if (this.statusType === 'info') {
                this.statusMessage = '';
                this.statusType = '';
              }
            }, 4000);
          }
        })
        .catch(() => {});
    },
    stopBillMailLiveSubscription() {
      if (!this.billMailLiveSubscribed || !this.companyId) {
        this.billMailLiveSubscribed = false;
        return;
      }
      import('@/services/companyPresenceClient')
        .then((m) => m.unsubscribeBillMailLive(this.companyId))
        .catch(() => {});
      this.billMailLiveSubscribed = false;
    },
    handleBillMailSocketRefresh(event) {
      const detail = event?.detail || {};
      const cid = Number(detail.companyId);
      if (Number.isFinite(cid) && Number(this.companyId) !== cid) return;
      if (!this.isLiveSyncMode || !this.isActiveServiceConnected) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      this.loadEmails({ silent: true });
    },
    reloadEmails() {
      return this.loadEmails();
    },
    selectService(service) {
      this.activeService = service;
      this.statusMessage = '';
      this.statusType = '';
    },
    senderName(email) {
      if (email.fromName) return email.fromName;
      const raw = email.from || email.fromEmail || 'Unknown';
      if (typeof raw === 'object') {
        return raw.name || raw.emailAddress?.name || raw.address || 'Unknown';
      }
      const match = String(raw).match(/^(.+?)\s*<[^>]+>$/);
      if (match) return match[1].replace(/"/g, '').trim();
      if (String(raw).includes('@')) return String(raw).split('@')[0];
      return String(raw);
    },
    resolveSenderEmail(email) {
      if (!email) return '';
      const raw = email.from || email.fromEmail || '';
      if (typeof raw === 'object') {
        return raw.emailAddress?.address || raw.address || raw.email || '';
      }
      const match = String(raw).match(/<([^>]+)>/);
      if (match) return match[1];
      return String(raw).includes('@') ? String(raw) : String(raw || email.fromName || '');
    },
    senderInitials(email) {
      return nameInitials(this.senderName(email), '?');
    },
    withCompanyQuery(url) {
      if (!this.companyId) return url;
      const join = url.includes('?') ? '&' : '?';
      return `${url}${join}companyId=${encodeURIComponent(this.companyId)}`;
    },
    async checkEmailServiceAuth() {
      try {
        const gmailResponse = await fetch(`${process.env.VUE_APP_API_URL}/gmail/auth/status`);
        if (gmailResponse.ok) {
          const gmailData = await gmailResponse.json();
          this.gmailAuthenticated = gmailData.authenticated || false;
          if (this.gmailAuthenticated) {
            await this.fetchUserEmail('gmail');
          } else {
            this.gmailUserEmail = '';
          }
        }
      } catch (error) {
        logger.error('Error checking Gmail auth:', error);
        this.gmailAuthenticated = false;
        this.gmailUserEmail = '';
      }

      try {
        const outlookResponse = await fetch(
          this.withCompanyQuery(`${process.env.VUE_APP_API_URL}/outlook/auth/status`)
        );
        if (outlookResponse.ok) {
          const outlookData = await outlookResponse.json();
          this.outlookAuthenticated = outlookData.authenticated || false;
          if (this.outlookAuthenticated) {
            this.outlookUserEmail = outlookData.email || this.outlookUserEmail;
            await this.fetchUserEmail('outlook');
          } else {
            this.outlookUserEmail = '';
          }
        }
      } catch (error) {
        logger.error('Error checking Outlook auth:', error);
        this.outlookAuthenticated = false;
        this.outlookUserEmail = '';
      }

      if (this.outlookAuthenticated) {
        this.activeService = 'outlook';
      } else if (this.gmailAuthenticated) {
        this.activeService = 'gmail';
      }

      if (this.isActiveServiceConnected) {
        if (hasConfiguredMailSources(this.fetchRules)) {
          this.loadEmails();
        } else {
          // Parent may still be loading mail settings after a page refresh.
          // Never auto-route to Bill Mail Setup — user opens it via the button.
          this.emails = [];
          this.selectedEmail = null;
        }
      }
    },
    async fetchUserEmail(service) {
      const endpoint = service === 'gmail'
        ? '/gmail/user'
        : this.withCompanyQuery('/outlook/user');
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}${endpoint}`);
        if (response.ok) {
          const userData = await response.json();
          const email = userData.email || '';
          if (service === 'gmail') {
            this.gmailUserEmail = email;
          } else {
            this.outlookUserEmail = email;
          }
        }
      } catch (error) {
        logger.error(`Error fetching ${service} user email:`, error);
      }
    },
    async handleConnect() {
      this.connecting = true;
      this.statusMessage = '';
      this.statusType = '';
      const service = this.activeService;
      const endpoint = service === 'gmail'
        ? '/gmail/auth'
        : this.withCompanyQuery('/outlook/auth');

      try {
        if (service === 'outlook' && !this.companyId) {
          throw new Error('Company ID is required to connect Outlook');
        }
        const response = await fetch(`${process.env.VUE_APP_API_URL}${endpoint}`);
        if (!response.ok) {
          throw new Error(`Failed to get ${service} authentication URL`);
        }
        const data = await response.json();
        if (data.authUrl) {
          window.open(data.authUrl, '_blank', 'width=600,height=700');
          this.statusMessage = 'Complete authorization in the popup window. Connection will stay active with auto-refresh.';
          this.statusType = 'info';
          this.startAuthCheck();
        } else {
          throw new Error('Authentication URL not received');
        }
      } catch (error) {
        logger.error(`Error connecting ${service}:`, error);
        this.statusMessage = error.message;
        this.statusType = 'error';
      } finally {
        this.connecting = false;
      }
    },
    async handleDisconnect() {
      this.disconnecting = true;
      const service = this.activeService;
      const base = service === 'gmail' ? '/gmail/auth' : '/outlook/auth';
      const disconnectUrl = service === 'outlook'
        ? this.withCompanyQuery(`${process.env.VUE_APP_API_URL}${base}/disconnect`)
        : `${process.env.VUE_APP_API_URL}${base}/disconnect`;

      try {
        let disconnectSuccessful = false;
        try {
          const response = await fetch(disconnectUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: service === 'outlook' && this.companyId
              ? JSON.stringify({ companyId: this.companyId })
              : undefined,
          });
          if (response.ok) disconnectSuccessful = true;
        } catch (e) {
          logger.log('Disconnect endpoint not available');
        }

        if (!disconnectSuccessful) {
          try {
            const revokeUrl = service === 'outlook'
              ? this.withCompanyQuery(`${process.env.VUE_APP_API_URL}${base}/revoke`)
              : `${process.env.VUE_APP_API_URL}${base}/revoke`;
            const response = await fetch(revokeUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: service === 'outlook' && this.companyId
                ? JSON.stringify({ companyId: this.companyId })
                : undefined,
            });
            if (response.ok) disconnectSuccessful = true;
          } catch (e) {
            logger.log('Revoke endpoint not available');
          }
        }

        if (service === 'gmail') {
          this.gmailAuthenticated = false;
          this.gmailUserEmail = '';
        } else {
          this.outlookAuthenticated = false;
          this.outlookUserEmail = '';
        }

        this.emails = [];
        this.selectedEmail = null;
        this.statusMessage = disconnectSuccessful
          ? 'Disconnected successfully.'
          : 'Disconnected locally.';
        this.statusType = disconnectSuccessful ? 'success' : 'info';
      } catch (error) {
        logger.error(`Error disconnecting ${service}:`, error);
        this.statusMessage = error.message;
        this.statusType = 'error';
      } finally {
        this.disconnecting = false;
      }
    },
    startAuthCheck() {
      this.clearAuthCheckInterval();
      this.authCheckInterval = setInterval(async () => {
        await this.checkEmailServiceAuth();
        if (this.isActiveServiceConnected) {
          this.clearAuthCheckInterval();
          this.statusMessage = 'Connected successfully.';
          this.statusType = 'success';
          setTimeout(() => {
            this.statusMessage = '';
            this.statusType = '';
          }, 4000);
        }
      }, 3000);
    },
    clearAuthCheckInterval() {
      if (this.authCheckInterval) {
        clearInterval(this.authCheckInterval);
        this.authCheckInterval = null;
      }
    },
    async loadDoneMessageIds() {
      if (!this.companyId) return;
      try {
        const url = this.withCompanyQuery(`${process.env.VUE_APP_API_URL}/outlook/bill-mail/done-ids`);
        const response = await fetch(url);
        if (!response.ok) return;
        const result = await response.json().catch(() => ({}));
        const ids = Array.isArray(result.data) ? result.data : [];
        const next = {};
        ids.forEach((id) => {
          if (id) next[String(id)] = true;
        });
        this.doneMessageIds = next;
      } catch (error) {
        logger.error('Could not load completed captured mail ids:', error);
      }
    },
    excludeDoneMail(emails) {
      const done = this.doneMessageIds || {};
      return (emails || []).filter((email) => email?.id && !done[String(email.id)]);
    },
    async ingestCapturedMailReviews(emails) {
      if (this.activeService !== 'outlook' || !this.companyId) return;
      const ids = (emails || [])
        .filter((email) => email?.id && email.hasAttachments && !this.doneMessageIds[String(email.id)])
        .map((email) => String(email.id));
      const fresh = ids.filter((id) => !this.lastIngestedIds[id]);
      if (!fresh.length) return;

      try {
        const url = this.withCompanyQuery(`${process.env.VUE_APP_API_URL}/outlook/bill-mail/ingest`);
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId: this.companyId,
            messageIds: fresh,
          }),
        });
        if (!response.ok) return;
        const next = { ...this.lastIngestedIds };
        fresh.forEach((id) => {
          next[id] = true;
        });
        this.lastIngestedIds = next;
      } catch (error) {
        logger.error('Could not queue captured mail reviews:', error);
      }
    },
    async loadEmails(options = {}) {
      const silent = !!options.silent;
      if (!this.isActiveServiceConnected) return;
      if (silent && this.loadingEmails) return;

      if (!hasConfiguredMailSources(this.fetchRules)) {
        this.emails = [];
        this.selectedEmail = null;
        if (!silent) {
          this.statusMessage = 'Configure Bill Mail Setup to choose which senders to load.';
          this.statusType = 'info';
        }
        return;
      }

      this.loadingEmails = true;
      await this.loadDoneMessageIds();
      const endpoint = this.activeService === 'gmail' ? '/gmail/read' : '/outlook/read';
      const fetchSize = computeMailFetchSize(this.itemsPerPage);
      const mailQuery = buildMailFetchQuery(this.fetchRules, this.activeService);
      let url = `${process.env.VUE_APP_API_URL}${endpoint}?maxResults=${fetchSize}`;
      if (mailQuery) url += `&query=${encodeURIComponent(mailQuery)}`;
      if (this.activeService === 'outlook') url = this.withCompanyQuery(url);

      try {
        const response = await fetch(url);
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result.error || result.message || 'Failed to load emails');
        }
        const nextEmails = result.data || result.emails || result.messages || [];
        this.emails = this.excludeDoneMail(
          (Array.isArray(nextEmails) ? nextEmails : []).map((email) => ({
            ...email,
            mailService: this.activeService,
          }))
        );
        this.clampCurrentPage();
        this.ingestCapturedMailReviews(this.emails);
        if (!silent) {
          this.statusMessage = `Loaded ${this.emails.length} message(s). ${describeMailFetchRules(this.fetchRules)}`;
          this.statusType = 'success';
          setTimeout(() => {
            if (this.statusType === 'success') {
              this.statusMessage = '';
              this.statusType = '';
            }
          }, 5000);
        }
      } catch (error) {
        logger.error('Error loading emails:', error);
        if (!silent) {
          this.emails = [];
          this.statusMessage = `Could not load emails: ${error.message}`;
          this.statusType = 'error';
        }
      } finally {
        this.loadingEmails = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.selectedEmail = null;
    },
    handleItemsPerPageChange(itemsPerPage) {
      if (!MAIL_PER_PAGE_OPTIONS.includes(itemsPerPage)) return;

      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
      this.selectedEmail = null;

      try {
        localStorage.setItem(STORAGE_KEY_MAIL_PAGE_SIZE, String(itemsPerPage));
      } catch (e) {
        /* ignore */
      }

      if (this.isActiveServiceConnected) {
        this.loadEmails();
      }
    },
    clampCurrentPage() {
      const totalPages = Math.max(1, Math.ceil(this.filteredEmails.length / this.itemsPerPage));
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
    },
    async selectEmail(email) {
      this.selectedEmail = email;
      let url = `${process.env.VUE_APP_API_URL}/${this.activeService === 'gmail' ? 'gmail' : 'outlook'}/read/${email.id}`;
      if (this.activeService === 'outlook') url = this.withCompanyQuery(url);

      try {
        const response = await fetch(url);
        if (response.ok) {
          const result = await response.json();
          const details = result.data || result;
          this.selectedEmail = details
            ? { ...details, mailService: this.activeService }
            : this.selectedEmail;
        }
      } catch (error) {
        logger.error('Error loading email details:', error);
      }
    },
    formatFileSize(bytes) {
      const size = Number(bytes) || 0;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
    async downloadAttachment(attachment) {
      if (!this.selectedEmail?.id || !attachment?.id) return;

      this.downloadingAttachmentId = attachment.id;
      const service = this.activeService;
      const base = service === 'gmail' ? '/gmail/read' : '/outlook/read';
      let url = `${process.env.VUE_APP_API_URL}${base}/${this.selectedEmail.id}/attachments/${attachment.id}`;
      if (service === 'gmail') {
        url += `?filename=${encodeURIComponent(attachment.name || 'attachment')}&contentType=${encodeURIComponent(attachment.contentType || 'application/octet-stream')}`;
      } else {
        url = this.withCompanyQuery(url);
      }

      try {
        const response = await fetch(url);

        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || 'Failed to download attachment');
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = attachment.name || 'attachment';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        logger.error('Error downloading attachment:', error);
        this.statusMessage = `Could not download attachment: ${error.message}`;
        this.statusType = 'error';
      } finally {
        this.downloadingAttachmentId = null;
      }
    },
    formatDateShort(dateString) {
      return formatOutlookListDate(dateString);
    },
    formatDateFull(dateString) {
      return formatOutlookDetailDate(dateString);
    },
    bindMailBodyImages() {
      const root = this.$refs.mailBodyHtml;
      if (!root) return;
      root.querySelectorAll('img').forEach((img) => {
        if (img.dataset.mailBound === '1') return;
        img.dataset.mailBound = '1';
        img.addEventListener('error', () => {
          img.classList.add('mail-reader-body__img--broken');
          img.setAttribute('alt', '');
          img.removeAttribute('src');
        });
      });
    },
    recipientAvatarStyle,
    recipientDisplayName,
    nameInitials,
    attachmentFileIcon(name) {
      const ext = String(name || '').split('.').pop()?.toLowerCase() || '';
      if (ext === 'pdf') return 'bi bi-file-earmark-pdf-fill';
      if (['doc', 'docx'].includes(ext)) return 'bi bi-file-earmark-word-fill';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'bi bi-file-earmark-excel-fill';
      if (['ppt', 'pptx'].includes(ext)) return 'bi bi-file-earmark-ppt-fill';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'bi bi-file-earmark-image-fill';
      if (['zip', 'rar', '7z'].includes(ext)) return 'bi bi-file-earmark-zip-fill';
      return 'bi bi-file-earmark-fill';
    },
    attachmentCardClass(name) {
      const ext = String(name || '').split('.').pop()?.toLowerCase() || '';
      if (ext === 'pdf') return 'mail-attachment-card--pdf';
      if (['doc', 'docx'].includes(ext)) return 'mail-attachment-card--doc';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'mail-attachment-card--sheet';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'mail-attachment-card--image';
      return 'mail-attachment-card--file';
    },
  },
};
</script>
